import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService } from 'api/auth';
import { fetchLevels } from './levelSlice';
import { gamesApi } from './gamesApi';
import { paymentApi } from './paymentApi';
import { bonusesApi } from './bonusesApi';
import { showLoader, hideLoader } from './uiSlice';
import {
  AuthState,
  RegisterPayload,
  LoginPayload,
  Verify2FAPayload,
  TransferPayload,
  UserProfile,
} from 'types/auth';
import { formatLocalTime } from 'utils/formatDate';

const formatBlockedMessage = (msg: string) => {
  if (typeof msg !== 'string') return msg;
  if (msg.startsWith('Your account is blocked until ')) {
    const dateStr = msg.replace('Your account is blocked until ', '').replace(/\.$/, '').trim();
    const localDate = formatLocalTime(dateStr);
    return `Your account is blocked until ${localDate}.`;
  }
  return msg;
};

const isLoginInitial = typeof window !== 'undefined' && localStorage.getItem('isLogin') === 'true';

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: isLoginInitial, // Prevent premature redirects on page refresh
  isInitializing: isLoginInitial,
  error: null,
  justRegistered: false,
  requiresTwoFactor: false,
  twoFactorToken: null,
  isMyLevelLoading: false,
};

// Async thunk for fetching the current user profile on app load
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchUser',
  async (silent: boolean | undefined, { rejectWithValue }) => {
    try {
      const response = await authService.fetchCurrentUser();
      return response;
    } catch (err: any) {
      const status = err.response?.status;
      return rejectWithValue({ status, message: err.message });
    }
  },
);

// Async thunk for registering a new user
export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData: RegisterPayload, { rejectWithValue }) => {
    try {
      const response = await authService.registerUser(userData);
      // Expected response depends on API. Usually returns the created user object or a generic success message
      return response;
    } catch (err: any) {
      // Safely access error response from Axios
      const errorMessage = formatBlockedMessage(
        err.response?.data?.message || err.message || 'An error occurred during registration',
      );
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for logging in an existing user
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await authService.loginUser(credentials);
      dispatch(
        gamesApi.endpoints.getRecentlyPlayed.initiate(undefined, { forceRefetch: true }) as any,
      );
      return response;
    } catch (err: any) {
      const errorMessage = formatBlockedMessage(
        err.response?.data?.message || err.message || 'An error occurred during login',
      );
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for logging in with Google
export const loginWithGoogle = createAsyncThunk(
  'auth/googleLogin',
  async (idToken: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await authService.googleLogin(idToken);
      dispatch(
        gamesApi.endpoints.getRecentlyPlayed.initiate(undefined, { forceRefetch: true }) as any,
      );
      return response;
    } catch (err: any) {
      const errorMessage = formatBlockedMessage(
        err.response?.data?.message || err.message || 'An error occurred during Google login',
      );
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for verifying 2FA code during login
export const verify2FA = createAsyncThunk(
  'auth/verify2FA',
  async (payload: Verify2FAPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await authService.verify2FA(payload);
      dispatch(
        gamesApi.endpoints.getRecentlyPlayed.initiate(undefined, { forceRefetch: true }) as any,
      );
      return response;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'An error occurred during 2FA verification';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for logging out the user
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader({ text: 'Logging out...' }));
      await authService.logoutUser();
      dispatch(gamesApi.util.invalidateTags(['RecentlyPlayed']));
      dispatch(paymentApi.util.resetApiState());
      dispatch(bonusesApi.util.resetApiState());
      dispatch(fetchLevels() as any);

      // Hold the loader on screen for an extra 1.2 seconds to smoothly
      // cover the router redirect and homepage data refetch.
      setTimeout(() => {
        dispatch(hideLoader());
      }, 1200);
    } catch (err: any) {
      dispatch(hideLoader());
      return rejectWithValue('Logout failed');
    }
  },
);

// Async thunk for fetching the user's current level info
export const fetchMyLevel = createAsyncThunk(
  'auth/fetchMyLevel',
  async (options: { silent?: boolean } | void, { rejectWithValue }) => {
    try {
      const response = await authService.fetchMyLevel();
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch level data');
    }
  },
);

// Async thunk for spinning the level wheel
export const spinLevel = createAsyncThunk(
  'auth/spinLevel',
  async (arg: string | { levelKey: string; idempotencyKey?: string }, { rejectWithValue }) => {
    const levelKey = typeof arg === 'string' ? arg : arg.levelKey;
    const idempotencyKey = typeof arg === 'string' ? undefined : arg.idempotencyKey;
    try {
      const response = await authService.spinLevelMember(levelKey, idempotencyKey);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to spin the wheel');
    }
  },
);

// Async thunk for transferring balance between main and vault
export const transferBalance = createAsyncThunk(
  'auth/transferBalance',
  async (payload: TransferPayload, { rejectWithValue }) => {
    try {
      const response = await authService.transferBalance(payload, payload.idempotencyKey);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Transfer failed';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for claiming referral payout
export const claimReferralPayout = createAsyncThunk(
  'auth/claimReferralPayout',
  async (arg: { idempotencyKey?: string } | void, { rejectWithValue }) => {
    try {
      const response = await authService.claimReferralPayout(arg?.idempotencyKey);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to claim payout';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for fetching user balances
export const fetchUserBalance = createAsyncThunk(
  'auth/fetchBalance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.fetchBalance();
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch user balance');
    }
  },
);

// Auth Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    incrementUnreadNotificationCount: (state) => {
      if (state.user) {
        state.user.unreadNotificationCount = (state.user.unreadNotificationCount || 0) + 1;
      }
    },
    decrementUnreadNotificationCount: (state) => {
      if (
        state.user &&
        state.user.unreadNotificationCount &&
        state.user.unreadNotificationCount > 0
      ) {
        state.user.unreadNotificationCount -= 1;
      }
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setEmailVerified: (state) => {
      if (state.user) {
        state.user.isEmailVerified = true;
      }
    },
    clearJustRegistered: (state) => {
      state.justRegistered = false;
    },
    setPendingSelfExclusion: (state, action) => {
      if (state.user) {
        state.user.pendingSelfExclusion = action.payload;
      }
    },
    setRequiresTwoFactor: (state, action) => {
      state.requiresTwoFactor = action.payload;
      if (!action.payload) {
        state.twoFactorToken = null;
      }
    },
    updateLocalSpinState: (state, action: PayloadAction<string>) => {
      if (state.user?.level) {
        const spin = state.user.level.spin;
        if (spin?.additionalSpinCount && spin.additionalSpinCount > 0) {
          spin.additionalSpinCount -= 1;
        } else if (spin?.pendingSpinLevels) {
          spin.pendingSpinLevels = spin.pendingSpinLevels.filter((key) => key !== action.payload);
        }
        state.user.level.hasAvailableSpin =
          (spin?.pendingSpinLevels?.length ?? 0) > 0 || (spin?.additionalSpinCount ?? 0) > 0;
      }
    },
    updateBalances: (state, action) => {
      if (state.user && action.payload) {
        const { spin, balances } = action.payload;
        if (spin) {
          state.user.level = {
            ...state.user.level,
            hasAvailableSpin: spin.hasAvailableSpin,
          };

          state.user.level.spin.additionalSpinCount = spin.additionalSpinCount;
          state.user.level.spin.pendingSpinLevels = spin.pendingSpinLevels;
        }
        if (balances && balances.length > 0) {
          state.user.balances = balances.map((b: any) => ({
            balance: b.balance,
            currency: b.currency,
            vault: b.vault || '0.00',
            withdrawable: b.withdrawable || '0.00',
            vaultAvailable: b.vaultAvailable || '0.00',
          }));
        }
      }
    },
    updateSocketBalance: (state, action) => {
      if (state.user && state.user.balances && action.payload) {
        const payload = action.payload;
        let socketBalances: any[] = [];

        if (Array.isArray(payload)) {
          socketBalances = payload;
        } else if (payload.balances && Array.isArray(payload.balances)) {
          socketBalances = payload.balances;
        } else if (payload.currency) {
          // Handle single balance object: { currency: 'USD', balance: '123' }
          socketBalances = [payload];
        }

        if (socketBalances.length > 0) {
          state.user.balances = state.user.balances.map((existing) => {
            const update = socketBalances.find((b: any) => b.currency === existing.currency);
            if (update) {
              return {
                ...existing,
                balance: update.balance !== undefined ? update.balance : existing.balance,
                withdrawable:
                  update.withdrawable !== undefined ? update.withdrawable : existing.withdrawable,
              };
            }
            return existing;
          });
        }
      }
    },
    updateUserProfile: (
      state,
      action: PayloadAction<{ profile: UserProfile; isProfileVerified?: boolean }>,
    ) => {
      if (state.user) {
        state.user.profile = {
          ...(state.user.profile || {}),
          ...action.payload.profile,
        };
        if (action.payload.isProfileVerified !== undefined) {
          state.user.isProfileVerified = action.payload.isProfileVerified;
        }
      }
    },
    updateUserLanguage: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.language = action.payload;
        if (state.user.profile) {
          state.user.profile.language = action.payload;
        }
      }
      localStorage.setItem('language', action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Register cases
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;

        const responseData = action.payload.data;

        if (responseData) {
          state.isAuthenticated = true;
          state.justRegistered = true;
          state.user = {
            ...responseData,
          };
          localStorage.setItem('isLogin', 'true');
          const userLang = responseData.language || responseData.profile?.language;
          if (userLang) {
            localStorage.setItem('language', userLang);
          }
        }
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;

        const responseData = action.payload.data;

        if (responseData) {
          if (responseData.requiresTwoFactor) {
            state.requiresTwoFactor = true;
            state.twoFactorToken = responseData.twoFactorToken;
          } else {
            state.isAuthenticated = true;
            state.user = {
              ...responseData,
            };
            localStorage.setItem('isLogin', 'true');
            state.requiresTwoFactor = false;
            state.twoFactorToken = null;
            const userLang = responseData.language || responseData.profile?.language;
            if (userLang) {
              localStorage.setItem('language', userLang);
            }
          }
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })
      // Fetch User (App Load) cases
      .addCase(fetchCurrentUser.pending, (state, action) => {
        if (!action.meta.arg) {
          state.isLoading = true;
          state.isInitializing = true;
        }
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitializing = false;
        const responseData = action.payload.data;

        if (responseData) {
          state.isAuthenticated = true;
          state.user = {
            ...responseData,
          };
          const userLang = responseData.language || responseData.profile?.language;
          if (userLang) {
            localStorage.setItem('language', userLang);
          }
        }
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isInitializing = false;

        const payload = action.payload as { status?: number; message?: string } | null;

        if (payload?.status === 401 || payload?.status === 403) {
          state.isAuthenticated = false;
          state.user = null;
          localStorage.removeItem('isLogin');
        }

        state.error = null;
      })
      // Google Login cases
      .addCase(loginWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;

        const responseData = action.payload.data;

        if (responseData) {
          if (responseData.requiresTwoFactor) {
            state.requiresTwoFactor = true;
            state.twoFactorToken = responseData.twoFactorToken;
          } else {
            state.isAuthenticated = true;
            state.user = {
              ...responseData,
            };
            localStorage.setItem('isLogin', 'true');
            state.requiresTwoFactor = false;
            state.twoFactorToken = null;
            const userLang = responseData.language || responseData.profile?.language;
            if (userLang) {
              localStorage.setItem('language', userLang);
            }
          }
        }
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })
      // Logout cases
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        localStorage.setItem('isLogin', 'false');
        Object.keys(sessionStorage).forEach((key) => {
          if (key.startsWith('game_state_')) {
            sessionStorage.removeItem(key);
          }
        });
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if the server fails the logout (e.g., token already invalid),
        // we should still clear the local session.
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        localStorage.setItem('isLogin', 'false');
        Object.keys(sessionStorage).forEach((key) => {
          if (key.startsWith('game_state_')) {
            sessionStorage.removeItem(key);
          }
        });
      })
      // Verify 2FA cases
      .addCase(verify2FA.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verify2FA.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data;
        if (responseData) {
          state.isAuthenticated = true;
          state.user = {
            ...responseData,
          };
          localStorage.setItem('isLogin', 'true');
          state.requiresTwoFactor = false;
          state.twoFactorToken = null;
          const userLang = responseData.language || responseData.profile?.language;
          if (userLang) {
            localStorage.setItem('language', userLang);
          }
        }
      })
      .addCase(verify2FA.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch My Level cases
      .addCase(fetchMyLevel.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.isMyLevelLoading = true;
        }
      })
      .addCase(fetchMyLevel.fulfilled, (state, action) => {
        state.isMyLevelLoading = false;
        if (state.user) {
          state.user.level = action.payload.data;
        }
      })
      .addCase(fetchMyLevel.rejected, (state) => {
        state.isMyLevelLoading = false;
      })
      // Spin Level cases
      // (updateBalances handles state update after the spin animation ends)
      .addCase(transferBalance.fulfilled, (state, action) => {
        if (state.user && action.payload?.data) {
          const responseData = action.payload.data;
          const fromType = action.meta.arg?.fromType;
          if (state.user.balances) {
            state.user.balances = state.user.balances.map((b) => {
              if (b.currency === (responseData.currencyCode || b.currency)) {
                if (fromType === 'vault') {
                  return {
                    ...b,
                    balance: responseData.mainBalance ?? b.balance,
                    vault: responseData.vaultBalance ?? responseData.fromNewBalance ?? b.vault,
                    withdrawable: responseData.withdrawableBalance ?? b.withdrawable,
                    vaultLockedUntil:
                      responseData.lockedUntil ??
                      responseData.vaultLockedUntil ??
                      b.vaultLockedUntil,
                    vaultAvailable: (
                      Number(b.vaultAvailable) - Number(responseData.amount)
                    ).toFixed(2),
                  };
                } else {
                  return {
                    ...b,
                    balance: responseData.mainBalance ?? b.balance,
                    vault: responseData.vaultBalance ?? responseData.toNewBalance ?? b.vault,
                    withdrawable: responseData.withdrawableBalance ?? b.withdrawable,
                    vaultLockedUntil:
                      responseData.lockedUntil ??
                      responseData.vaultLockedUntil ??
                      b.vaultLockedUntil,
                    vaultAvailable: responseData.vaultAvailable ?? b.vaultAvailable,
                  };
                }
              }
              return b;
            });
          }
        }
      })
      .addCase(fetchUserBalance.fulfilled, (state, action) => {
        if (state.user && action.payload) {
          const responseData = action.payload.data;
          let newBalances = null;
          if (Array.isArray(responseData)) {
            newBalances = responseData;
          } else if (responseData?.balances && Array.isArray(responseData.balances)) {
            newBalances = responseData.balances;
          }

          if (newBalances && newBalances.length > 0) {
            state.user.balances = newBalances.map((b: any) => ({
              balance: b.balance,
              currency: b.currency,
              vault: b.vault || '0.00',
              withdrawable: b.withdrawable || '0.00',
              vaultAvailable: b.vaultAvailable || '0.00',
            }));
          }
        }
      });
  },
});

export const {
  clearError,
  setError,
  setEmailVerified,
  clearJustRegistered,
  setPendingSelfExclusion,
  setRequiresTwoFactor,
  updateLocalSpinState,
  updateBalances,
  updateSocketBalance,
  incrementUnreadNotificationCount,
  decrementUnreadNotificationCount,
  updateUserProfile,
  updateUserLanguage,
} = authSlice.actions;

export default authSlice.reducer;
